import type { Screen0LanguageCode } from "@/features/language/language-registry";
import { getScreen0IntlLocale } from "@/features/language/language-registry";
import { getTranslations } from "@/i18n";
import type { ChatTurn } from "../native/ai";
import { NativeOnDeviceAI } from "../native/plugins";
import { formatDuration, formatInsightTimeBandLabel, type InsightTimeBandKey } from "../format";

const NATIVE_CHAT_FAILURE = /on-device chat failed/i;

const LANGUAGE_NAMES: Record<Screen0LanguageCode, string> = {
  en: "English",
  it: "Italian",
  de: "German",
  fr: "French",
  es: "Spanish",
};

const TIME_BAND_KEYS: InsightTimeBandKey[] = ["morning", "afternoon", "evening", "night"];

const RAW_SECONDS_PATTERN =
  /(\d[\d.,]*)\s*(secondi|seconds|secondes|segundos|Sekunden)\b/gi;

const FORMATTING_EXAMPLES: Record<
  Screen0LanguageCode,
  { bad: (band: string) => string; good: (band: string, duration: string) => string }
> = {
  en: {
    bad: (band) => `The busiest time band is **${band}**, with **75034 seconds**.`,
    good: (band, duration) => `The busiest time band is ${band}, with about ${duration} of use.`,
  },
  it: {
    bad: (band) => `La fascia oraria più intensa è la **${band}**, con **75034 secondi**.`,
    good: (band, duration) => `La fascia più intensa è la ${band}, con circa ${duration} di utilizzo.`,
  },
  de: {
    bad: (band) => `Das intensivste Zeitfenster ist der **${band}**, mit **75034 Sekunden**.`,
    good: (band, duration) => `Am intensivsten ist der ${band}, mit etwa ${duration} Nutzung.`,
  },
  fr: {
    bad: (band) => `La plage horaire la plus intense est le **${band}**, avec **75034 secondes**.`,
    good: (band, duration) => `La plage la plus intense est le ${band}, avec environ ${duration} d'utilisation.`,
  },
  es: {
    bad: (band) => `La franja más intensa es la **${band}**, con **75034 segundos**.`,
    good: (band, duration) => `La franja más intensa es la ${band}, con unos ${duration} de uso.`,
  },
};

function formattingRules(languageCode: Screen0LanguageCode): string[] {
  const exampleBand = formatInsightTimeBandLabel("morning", languageCode);
  const exampleDuration = formatDuration(75034, languageCode);
  const examples = FORMATTING_EXAMPLES[languageCode] ?? FORMATTING_EXAMPLES.en;

  return [
    "Formatting rules (mandatory):",
    "- Reply in plain text only. NEVER use markdown: no **, _, bullets, or headings.",
    "- NEVER mention raw seconds or the word secondi/seconds/Sekunden/secondes/segundos.",
    `- Copy durations exactly as shown in the JSON (e.g. "${exampleDuration}").`,
    `- Time-band keys in JSON are already localized labels (e.g. "${exampleBand}"). Use those labels; never say morning/afternoon/evening/night.`,
    "- Tone: warm, calm, conversational coach — 2–4 short sentences unless the user asks for more.",
    "Examples:",
    `- BAD: "${examples.bad(exampleBand)}"`,
    `- GOOD: "${examples.good(exampleBand, exampleDuration)}"`,
  ];
}

export function buildInsightChatPrompt(
  message: string,
  contextJson: string,
  history: ChatTurn[],
  languageCode: Screen0LanguageCode,
): string {
  const locale = getScreen0IntlLocale(languageCode);
  const languageName = LANGUAGE_NAMES[languageCode] ?? "English";
  const historyBlock = history
    .slice(-8)
    .map((turn) => `${turn.role === "user" ? "User" : "Assistant"}: ${turn.text}`)
    .join("\n");

  return [
    "You are Screen0, a calm digital wellbeing assistant.",
    `Respond in ${languageName} (locale ${locale}).`,
    "Use ONLY the usage context JSON. Never invent data or mention other apps' message contents.",
    ...formattingRules(languageCode),
    "Use comparison for period-over-period questions (current vs previous equal-length window).",
    "Excluded apps affect Insight scores but raw daily totals may still include them.",
    `Usage context JSON:\n${contextJson}`,
    historyBlock ? `Recent chat:\n${historyBlock}` : "",
    `User question: ${message}`,
  ]
    .filter(Boolean)
    .join("\n\n");
}

/** Strip markdown artifacts and rewrite obvious raw-second or enum-key leaks. */
export function sanitizeInsightChatReply(
  reply: string,
  languageCode: Screen0LanguageCode,
): string {
  let text = reply.trim();
  if (!text) return text;

  text = text.replace(/\*\*([^*]+)\*\*/g, "$1");
  text = text.replace(/\*([^*]+)\*/g, "$1");
  text = text.replace(/__([^_]+)__/g, "$1");
  text = text.replace(/_([^_]+)_/g, "$1");

  text = text.replace(RAW_SECONDS_PATTERN, (_, numStr: string) => {
    const seconds = Number.parseInt(numStr.replace(/[.,]/g, ""), 10);
    if (Number.isFinite(seconds) && seconds > 0) {
      return formatDuration(seconds, languageCode);
    }
    return numStr;
  });

  for (const key of TIME_BAND_KEYS) {
    const label = formatInsightTimeBandLabel(key, languageCode);
    text = text.replace(new RegExp(`\\b${key}\\b`, "gi"), label);
  }

  return text;
}

export function mapInsightChatError(error: unknown, languageCode: Screen0LanguageCode): string {
  const t = getTranslations(languageCode);
  if (error instanceof Error) {
    if (NATIVE_CHAT_FAILURE.test(error.message)) return t.insightChat.unableToAnswer;
    if (error.message.trim()) return error.message;
  }
  return t.insightChat.unableToAnswer;
}

export async function insightLocalChatReply(
  message: string,
  contextJson: string,
  history: ChatTurn[],
  languageCode: Screen0LanguageCode,
): Promise<string> {
  const prompt = buildInsightChatPrompt(message, contextJson, history, languageCode);
  const result = await NativeOnDeviceAI.chatReply({ message, contextJson, prompt });
  const reply = result.reply?.trim();
  if (!reply) throw new Error("On-device AI did not return a reply. Try again.");
  return sanitizeInsightChatReply(reply, languageCode);
}
